import { CircleAlert, CircleCheck, CircleDashed } from "lucide-react";
import {
  ANSWER_CONFIDENCE_LABELS,
  ANSWER_CONFIDENCE_TONES,
  parseCiteMarkers,
  type AnswerConfidence,
  type ChatSource,
  type SemanticTone,
} from "@gb/contracts";
import { cn } from "@/lib/utils";
import { DISPLAY_LOCALE } from "@/lib/format";

/**
 * How well the sources supported the answer, above the answer.
 *
 * **The band is the hedge, so the prose does not have to be.** The answering
 * prompt still says "do not hedge to be safe" in those words — `product/0002`
 * §2.3 — and this is what makes that affordable: the doubt is one word a reader
 * can skim and act on, rather than a caveat sentence they have to find in
 * paragraph three and weigh for themselves. See `document-ai/0010`.
 *
 * Renders nothing when nothing assessed it. **Not assessed is not low**, and a
 * neutral grey band saying so would be a claim about the answer that nobody
 * made.
 */

/**
 * What each semantic tone looks like here.
 *
 * The classes are the console's and stay the console's — React Native has no
 * cascade, so this half cannot be shared. **Which tone a level gets** is the
 * half that can, and it comes from `ANSWER_CONFIDENCE_TONES` in the contract:
 * this file and the phone's answer screen held parallel maps coupled by a
 * comment, so nothing failed if one side escalated `needs-confirmation` to red
 * and the other did not.
 *
 * `bad` is unused by confidence today and declared anyway, because the tone
 * vocabulary is three-valued and a partial map here would push the next
 * vocabulary that needs red back into inventing its own.
 */
const TONE_CLASSES: Record<SemanticTone, { band: string; text: string }> = {
  ok: { band: "border-ok/30 bg-ok-bg", text: "text-ok" },
  warn: { band: "border-warn/30 bg-warn-bg", text: "text-warn" },
  bad: { band: "border-danger/30 bg-danger-bg", text: "text-danger" },
};

/** The glyph is per *level*, not per tone: `partial` and `needs-confirmation`
 *  share a colour and say different things, and the icon is what says it. */
const ICONS: Record<AnswerConfidence, typeof CircleCheck> = {
  high: CircleCheck,
  partial: CircleDashed,
  "needs-confirmation": CircleAlert,
};

export function ConfidenceBand({
  confidence,
  note,
  sources,
  answer,
}: {
  confidence: AnswerConfidence | null;
  note: string | null;
  sources: readonly ChatSource[];
  /** The answer's text, so the band counts the sources it cites. */
  answer?: string;
}) {
  if (!confidence) return null;
  const tone = {
    ...TONE_CLASSES[ANSWER_CONFIDENCE_TONES[confidence]],
    icon: ICONS[confidence],
  };
  const Icon = tone.icon;

  return (
    // A header *inside* the answer panel, not a floating chip: square top
    // corners and a bottom border, so it reads as the panel's own banner rather
    // than as a second card stacked above it.
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-6 py-3",
        tone.band,
      )}
    >
      <span className={cn("flex items-center gap-2", tone.text)}>
        <Icon size={16} aria-hidden />
        <span className="font-display text-[0.7rem] font-semibold tracking-[0.1em] uppercase">
          {ANSWER_CONFIDENCE_LABELS[confidence]}
        </span>
      </span>
      <span className="text-[0.82rem] text-muted">{basisLine(sources, answer)}</span>
      {note && (
        // The reason, not a second opinion. A level with nothing behind it is
        // unarguable — a reader who disagrees with "needs confirmation" has
        // nothing to go and check.
        //
        // **One line when the model obeys, two when it does not — never a
        // clipped sentence.** This was a header on a fixed-height panel, so
        // every line it took was a line the answer did not get, and an
        // unbounded note was observed eating four of them. The panel grows
        // with its answer since 2026-09-27 (`product/0013`); the clamp stays,
        // because a band four lines deep pushes the answer down the page all
        // the same. But `truncate` was worse than the
        // problem it fixed: a ten-word budget overshot to fourteen and the
        // reader got *"…but 24 are listed as currently on s…"*, which is a
        // reason nobody can act on and no signal that it was cut.
        //
        // The prompt asks for ten words, which fits one line. `line-clamp-2` is
        // the backstop for when it does not oblige, and the whole note stays on
        // `title` either way.
        <span
          title={note}
          className="min-w-0 flex-1 text-[0.8rem] leading-snug text-ink line-clamp-2"
        >
          {note}
        </span>
      )}
    </div>
  );
}

/**
 * What the level was judged on, derived rather than stored.
 *
 * Both halves are already on the sources: how many there were, and the most
 * recent date among them. Storing either would be a second copy of a fact that
 * can only drift — and `ChatSource.date` is described as load-bearing for
 * exactly this kind of ordering.
 *
 * **With the answer's text, it counts the sources the answer cites**
 * (2026-09-26, Phase 5): a source cited in the text, or in a note between
 * steps (`noteStep`). Those are what the review judged. A source returned and
 * never cited is in its step's detail, not in this count.
 *
 * An upload day is not a date a file is from (`dateKind: "uploaded"`, R87),
 * so it cannot be "last confirmed".
 */
export function basisLine(sources: readonly ChatSource[], answer?: string): string {
  const cited = answer === undefined ? null : new Set(parseCiteMarkers(answer));
  const counts = cited === null ? sources : sources.filter((source) => cited.has(source.n) || source.noteStep !== undefined);
  const count = counts.length;
  const counted = count === 1 ? "1 source" : `${count} sources`;

  // Lexicographic max over `YYYY-MM-DD`, which is the same as chronological
  // and needs no parsing. Undated sources simply do not participate.
  const latest = counts.reduce<string | null>(
    (newest, source) =>
      source.date && source.dateKind !== "uploaded" && (!newest || source.date > newest) ? source.date : newest,
    null,
  );
  if (!latest) return counted;

  return `${counted} · last confirmed ${formatDay(latest)}`;
}

/** `"2026-06-03"` → `"3 Jun"`. Parsed as UTC so it cannot slide a day. */
function formatDay(iso: string): string {
  const date = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(DISPLAY_LOCALE, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}
